import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Inbox, MessageSquare, Send, X, Loader2, RefreshCw, Filter } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { brand } from "../../context/CompanyContext";

/**
 * Dedicated "Pedidos de contacto" admin page.
 *
 * Replaces the cramped contact list inside AdminEmails — this page is fully
 * focused on inbound contact requests (form submissions + AI escalations).
 *
 * - Filter chips: Todos / Não lidos / AI escalados.
 * - Click a row to open the detail modal with reply form + history.
 * - Polls every 15s so new requests appear without manual refresh.
 */

const STORAGE_FILTER = "luxstay_admin_contacts_filter";

export default function AdminContactRequests() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem(STORAGE_FILTER) || "all"; } catch { return "all"; }
  });
  const [open, setOpen] = useState(null);

  const filtered = useMemo(() => {
    if (filter === "unread") return items.filter((c) => !c.read);
    if (filter === "ai") return items.filter((c) => c.source === "ai_chat_escalation");
    return items;
  }, [items, filter]);

  async function refresh() {
    try {
      const { data } = await api.get("/admin/contact-requests");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, []);

  function applyFilter(v) {
    setFilter(v);
    try { localStorage.setItem(STORAGE_FILTER, v); } catch { /* ignore */ }
  }

  async function markRead(id) {
    try {
      await api.post(`/admin/contact-requests/${id}/read`);
      setItems((prev) => prev.map((c) => c.id === id ? { ...c, read: true } : c));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function openContact(c) {
    setOpen(c);
    if (!c.read) markRead(c.id);
  }

  return (
    <div className="space-y-8" data-testid="admin-contacts-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <Inbox size={14} /> Admin · Pedidos de contacto
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">
            Pedidos recebidos
            {unread > 0 && (
              <span className="ml-3 align-middle inline-flex items-center justify-center min-w-[36px] h-9 px-2 text-base font-display bg-[#ef4444] text-white rounded-sm animate-pulse-slow"
                    data-testid="contacts-unread-pill">
                {unread}
              </span>
            )}
          </h1>
          <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
            Mensagens enviadas pelo formulário público e pedidos escalados pela
            assistente IA. Clique para abrir e responder diretamente — a
            resposta vai para o email do cliente automaticamente.
          </p>
        </div>
        <button onClick={refresh}
                className="px-3 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] inline-flex items-center gap-2"
                data-testid="contacts-refresh">
          <RefreshCw size={12} /> Recarregar
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="contacts-filter">
        <Filter size={12} className="text-[#a0b2af]" />
        <Chip active={filter === "all"} onClick={() => applyFilter("all")} testId="filter-all">
          Todos ({items.length})
        </Chip>
        <Chip active={filter === "unread"} onClick={() => applyFilter("unread")} testId="filter-unread" red={unread > 0}>
          Não lidos ({unread})
        </Chip>
        <Chip active={filter === "ai"} onClick={() => applyFilter("ai")} testId="filter-ai">
          IA escalados ({items.filter((c) => c.source === "ai_chat_escalation").length})
        </Chip>
      </div>

      {loading ? (
        <div className="text-sm text-[#a0b2af]">A carregar…</div>
      ) : filtered.length === 0 ? (
        <div className="lux-card p-8 text-center text-sm text-[#a0b2af]"
             data-testid="contacts-empty">
          {filter === "all"
            ? "Ainda não há pedidos. Assim que alguém enviar o formulário do site, aparece aqui."
            : "Nenhum pedido neste filtro."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="contacts-list">
          {filtered.map((c) => (
            <article key={c.id}
                     className={`lux-card p-4 cursor-pointer transition-colors hover:border-[#d4af37] ${!c.read ? "border-[#ef4444]/50 bg-[#ef4444]/5" : ""}`}
                     onClick={() => openContact(c)}
                     data-testid={`contact-row-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#fdfbf7] flex items-center gap-2 flex-wrap">
                    {!c.read && (
                      <span className="inline-block w-2 h-2 rounded-full bg-[#ef4444] animate-pulse"
                            aria-label="Não lido" />
                    )}
                    <strong>{c.name}</strong>
                    <span className="text-[#d4af37] text-xs">{c.email}</span>
                    {c.phone && <span className="text-[#a0b2af] text-xs font-mono">· {c.phone}</span>}
                    {c.source === "ai_chat_escalation" && (
                      <span className="text-[10px] uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] px-2 py-0.5">
                        IA
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#a0b2af] mt-1 line-clamp-2 whitespace-pre-wrap">{c.message}</p>
                  <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] mt-2 flex items-center gap-3 flex-wrap">
                    <span>{new Date(c.created_at).toLocaleString("pt-PT")}</span>
                    {c.replies?.length > 0 && (
                      <span className="text-[#10b981]" data-testid={`contact-replied-${c.id}`}>
                        ✓ {c.replies.length} resposta(s)
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-[#a0b2af] shrink-0 inline-flex items-center gap-1 text-xs">
                  <MessageSquare size={12} /> Ver / Responder
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {open && (
        <ContactDetailModal contact={open} onClose={() => setOpen(null)}
                            onReplied={(updated) => { setOpen(updated); refresh(); }} />
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testId, red }) {
  return (
    <button type="button" onClick={onClick}
            className={`px-3 py-1.5 border text-[11px] uppercase tracking-widest transition-colors ${
              active
                ? red ? "border-[#ef4444] text-[#ef4444] bg-[#ef4444]/10" : "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                : "border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37]"
            }`}
            data-testid={testId}>
      {children}
    </button>
  );
}

function ContactDetailModal({ contact, onClose, onReplied }) {
  const [subject, setSubject] = useState(`Re: o seu pedido — ${brand()}`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function send(e) {
    e.preventDefault();
    if (subject.trim().length < 2) return toast.error("Indique um assunto.");
    if (message.trim().length < 5) return toast.error("Escreva uma resposta com pelo menos 5 caracteres.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/contact-requests/${contact.id}/reply`, {
        subject: subject.trim(),
        message: message.trim(),
      });
      toast.success("Resposta enviada por email.");
      setMessage("");
      onReplied?.({
        ...contact,
        replies: [...(contact.replies || []), data.reply],
        read: true,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none overflow-y-auto">
        <div className="lux-card bg-[#041f1b] border border-[#1a4640] w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto my-auto"
             data-testid="contact-detail-modal">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a4640] sticky top-0 bg-[#041f1b] z-10">
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[#d4af37] flex items-center gap-2">
                <Inbox size={14} /> Pedido de contacto
                {contact.source === "ai_chat_escalation" && (
                  <span className="text-[9px] uppercase border border-[#d4af37]/40 text-[#d4af37] px-1.5 py-0.5">
                    Escalado pela IA
                  </span>
                )}
              </div>
              <div className="text-sm text-[#fdfbf7] mt-1 truncate">
                {contact.name} · <span className="text-[#a0b2af]">{contact.email}</span>
              </div>
            </div>
            <button type="button" onClick={onClose}
                    className="p-2 hover:text-[#d4af37] text-[#a0b2af]"
                    data-testid="contact-detail-close">
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-5">
            <section className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info k="Nome" v={contact.name} />
              <Info k="Email" v={<a href={`mailto:${contact.email}`} className="text-[#d4af37] hover:underline break-all">{contact.email}</a>} />
              <Info k="Telefone" v={contact.phone || "—"} />
              <Info k="Recebido" v={new Date(contact.created_at).toLocaleString("pt-PT")} />
            </section>

            <section>
              <div className="eyebrow mb-2 text-[#a0b2af]">Mensagem original</div>
              <div className="p-4 border border-[#1a4640] bg-[#062c27] rounded-sm text-sm text-[#fdfbf7] whitespace-pre-wrap"
                   data-testid="contact-original-message">
                {contact.message}
              </div>
            </section>

            {contact.replies?.length > 0 && (
              <section data-testid="contact-replies-history">
                <div className="eyebrow mb-2 text-[#a0b2af]">Respostas enviadas</div>
                <div className="space-y-2">
                  {contact.replies.map((r) => (
                    <div key={r.id} className="p-4 border-l-2 border-[#10b981]/60 bg-[#10b981]/5 rounded-sm">
                      <div className="text-[10px] uppercase tracking-widest text-[#10b981] mb-1.5">
                        {new Date(r.sent_at).toLocaleString("pt-PT")} · {r.sent_by_name || "Admin"}
                      </div>
                      <div className="text-sm text-[#fdfbf7] mb-1.5"><strong>Assunto:</strong> {r.subject}</div>
                      <div className="text-sm text-[#cde3df] whitespace-pre-wrap">{r.message}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <div className="eyebrow mb-2 text-[#d4af37]">
                {contact.replies?.length > 0 ? "Enviar nova resposta" : "Responder ao cliente"}
              </div>
              <form onSubmit={send} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Assunto</label>
                  <input type="text" value={subject} required maxLength={200}
                         onChange={(e) => setSubject(e.target.value)}
                         className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none"
                         data-testid="contact-reply-subject" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Mensagem</label>
                  <textarea value={message} required minLength={5} maxLength={4000} rows={6}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Olá, em resposta ao seu pedido…"
                            className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none resize-none"
                            data-testid="contact-reply-message" />
                  <div className="text-[10px] text-[#a0b2af] text-right">{message.length}/4000</div>
                </div>
                <div className="flex justify-end">
                  <button type="submit" disabled={busy}
                          className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                          data-testid="contact-reply-submit">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {busy ? "A enviar…" : "Enviar resposta"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function Info({ k, v }) {
  return (
    <div className="flex flex-col gap-1 p-3 border border-[#1a4640]">
      <dt className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{k}</dt>
      <dd className="text-sm text-[#fdfbf7]">{v || "—"}</dd>
    </div>
  );
}
